import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ClearIcon from '@mui/icons-material/Clear';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import React from 'react';
import { uid } from 'react-uid';
import type { ChargePointSummary } from '../../app/ApiGen';
import { List, ListHeader, ListItem } from '../../design-system';
import type { Normalised } from '../../utils/request';
import { AutocompleteOption } from '../AutocompleteOption';
import { TextField } from '../TextField';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// TODO: This type won't be necessary when getOrganisationChargePoints returns normalisable ChargePoint DTOs
export type SelectableChargePoint = Pick<Normalised<ChargePointSummary>, 'serial' | 'name' | 'id'>;

interface ChargePointsSelectProps {
  chargePoints: SelectableChargePoint[];
  preSelectedChargePoints: SelectableChargePoint[];
  onClear?: () => void;
  onChangeSelection?: (selected: SelectableChargePoint[]) => void;
}

function chargePointToString(chargePoint: SelectableChargePoint): string {
  return chargePoint.serial ? `Name: ${chargePoint.name}, Serial: ${chargePoint.serial}` : `Name: ${chargePoint.name}`;
}

function sortBySerial(chargePoint1: SelectableChargePoint, chargePoint2: SelectableChargePoint): number {
  const serial1 = chargePoint1.serial ?? '';
  const serial2 = chargePoint2.serial ?? '';
  if (serial1 === serial2) {
    const name1 = chargePoint1.name ?? '';
    const name2 = chargePoint2.name ?? '';
    return name1 < name2 ? -1 : 1;
  }
  return serial1 < serial2 ? -1 : 1;
}

export const ChargePointMultiSelect: React.FC<ChargePointsSelectProps> = (props: ChargePointsSelectProps) => {
  const { chargePoints, onClear, onChangeSelection, preSelectedChargePoints } = props;
  const [selectedChargePoints, setSelectedChargePoints] =
    React.useState<SelectableChargePoint[]>(preSelectedChargePoints);

  function unselectChargePoint(chargePoint: SelectableChargePoint): void {
    const filteredChargePoints = selectedChargePoints.filter((selected) => selected.id !== chargePoint.id);
    setSelectedChargePoints(filteredChargePoints);

    if (onChangeSelection) {
      onChangeSelection(filteredChargePoints);
    }
  }

  const renderChargePointItem = (chargePoint: SelectableChargePoint) => (
    <ListItem key={chargePoint.id}>
      <Grid container>
        <Grid item xs={5}>
          {chargePoint.name}
        </Grid>
        <Grid item xs={6}>
          {chargePoint.serial}
        </Grid>
        <Grid item xs={1}>
          <IconButton aria-label="remove charge point" size="small" onClick={() => unselectChargePoint(chargePoint)}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
    </ListItem>
  );

  return (
    <Grid container>
      <Grid item xs={12}>
        <Autocomplete<SelectableChargePoint, true, true>
          multiple
          disableClearable
          fullWidth
          selectOnFocus
          handleHomeEndKeys
          disableCloseOnSelect
          options={chargePoints}
          value={selectedChargePoints}
          getOptionLabel={chargePointToString}
          renderOption={(
            {
              'aria-disabled': ariaDisabled,
              'aria-selected': ariaSelected,
              className,
              onClick,
              onMouseMove,
              onTouchStart,
              role,
              tabIndex,
            },
            option,
            state,
          ) => (
            <AutocompleteOption
              aria-disabled={ariaDisabled}
              aria-selected={ariaSelected}
              className={className}
              index={state.index}
              key={uid(option)}
              onClick={onClick}
              onMouseMove={onMouseMove}
              onTouchStart={onTouchStart}
              role={role}
              tabIndex={tabIndex}
            >
              <Checkbox size="small" color="primary" icon={icon} checkedIcon={checkedIcon} checked={state.selected} />
              {chargePointToString(option)}
            </AutocompleteOption>
          )}
          renderInput={({ InputLabelProps, InputProps, disabled, fullWidth, id, inputProps }) => (
            <TextField
              InputLabelProps={InputLabelProps}
              InputProps={InputProps}
              className="textfield-root"
              disabled={disabled}
              fullWidth={fullWidth}
              id={id}
              inputElementProps={inputProps}
              label="Select charge points"
              name="chargePoints"
            />
          )}
          renderTags={() => null}
          onChange={(_, value, reason) => {
            setSelectedChargePoints(value);

            if (reason === 'clear' && onClear) {
              onClear();
            }
            if ((reason === 'selectOption' || reason === 'removeOption') && onChangeSelection) {
              onChangeSelection(value);
            }
          }}
        />
      </Grid>
      <Grid item xs={12}>
        {selectedChargePoints.length > 0 && (
          <List variant="bordered">
            <ListHeader>
              <Grid container>
                <Grid item xs={5}>
                  Name
                </Grid>
                <Grid item xs={5}>
                  Serial
                </Grid>
                <Grid item xs={1}>
                  Remove
                </Grid>
              </Grid>
            </ListHeader>
            {selectedChargePoints.sort(sortBySerial).map(renderChargePointItem)}
          </List>
        )}
      </Grid>
    </Grid>
  );
};
